import { useEffect, useState, useCallback } from "react";
import api, { apiErr, photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { WORKORDERS } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { WO_STATUSES, prettyStatus, fmtDate, fmtDateTime } from "@/lib/format";
import { Plus, ChevronRight, CheckCircle2, ArrowRight, HardHat, Camera, ClipboardList, ArrowLeftRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function WorkOrdersWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const siteId = ws.siteId;
  const canEdit = hasRole("tenant_admin", "contract_manager", "supervisor");
  const canVerify = hasRole("tenant_admin", "contract_manager", "supervisor");
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    const r = await api.get(`/work-orders?site_id=${siteId}`);
    setRows(r.data);
  }, [siteId]);
  useEffect(() => { load(); api.get("/team/users").then((r) => setMembers(r.data)).catch(() => {}); }, [load]);

  const save = async () => {
    try {
      await api.post("/work-orders", {
        title: form.title, description: form.description, site_id: siteId,
        contract_id: ws.contractId || null, priority: form.priority || "medium",
        assigned_to: form.assigned_to === "none" ? null : form.assigned_to,
        scheduled_date: form.scheduled_date || null, status: form.status || "draft",
      });
      toast.success("Work order created"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const advance = async (wo) => {
    const idx = WO_STATUSES.indexOf(wo.status);
    const next = WO_STATUSES[Math.min(idx + 1, WO_STATUSES.length - 1)];
    if (next === "completed") { toast.info("Completion is captured from the field (Field Mode)."); return; }
    if (next === "verified") { await verify(wo); return; }
    try { await api.patch(`/work-orders/${wo.id}`, { status: next }); toast.success(`Moved to ${prettyStatus(next)}`); load(); if (detail?.id === wo.id) openDetail(wo.id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const verify = async (wo) => {
    try { await api.post(`/work-orders/${wo.id}/verify`); toast.success("Verified"); load(); if (detail?.id === wo.id) openDetail(wo.id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openDetail = async (id) => {
    const r = await api.get(`/work-orders/${id}`);
    setDetail(r.data);
  };

  if (!siteId) return <p className="text-sm text-muted-foreground">Select a site to see its work orders.</p>;

  const zoneId = ws.zoneId;
  const visibleRows = zoneId ? rows.filter((w) => w.zone_id === zoneId) : rows;

  return (
    <div className="space-y-4" data-testid={WORKORDERS.root}>
      {zoneId && (
        <p className="flex items-center gap-1.5 rounded-lg border border-sage-light bg-sage-surface px-3 py-1.5 text-xs text-forest-dark" data-testid="work-orders-zone-focus">
          Focused on zone <b>{ws.zone?.name}</b> — switch to “All zones” in the top bar to see the whole site.
        </p>
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{visibleRows.length} work order{visibleRows.length === 1 ? "" : "s"} {zoneId ? "in this zone" : "on this site"}</p>
        {canEdit && <Button size="sm" onClick={() => { setForm({}); setOpen(true); }} data-testid={WORKORDERS.addBtn} className="bg-forest-mid hover:bg-forest-dark"><Plus className="mr-1 h-4 w-4" /> New work order</Button>}
      </div>

      <Card className="border-sage-light">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead><TableHead>Status</TableHead><TableHead>Assigned</TableHead>
                <TableHead>Scheduled</TableHead><TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No work orders{zoneId ? " in this zone" : ""}. Raise one here or from Diagnose.</TableCell></TableRow>}
              {visibleRows.map((w) => (
                <TableRow key={w.id} className="cursor-pointer" onClick={() => openDetail(w.id)} data-testid={WORKORDERS.row(w.id)}>
                  <TableCell className="font-medium text-forest-dark">{w.title}</TableCell>
                  <TableCell><span data-testid={WORKORDERS.statusBadge(w.id)}><StatusBadge status={w.status} /></span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.assigned_to_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(w.scheduled_date)}</TableCell>
                  <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New work order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={WORKORDERS.field("title")} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={form.priority || "medium"} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid={WORKORDERS.field("priority")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Initial status</Label>
                <Select value={form.status || "draft"} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid={WORKORDERS.field("status")}><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "triaged", "approved", "scheduled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{prettyStatus(s)}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Assign to</Label>
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v })}><SelectTrigger data-testid={WORKORDERS.field("assigned")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— unassigned —</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role_label})</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Scheduled date</Label><Input type="date" value={form.scheduled_date || ""} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid={WORKORDERS.field("scheduled")} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-forest-mid hover:bg-forest-dark" data-testid={WORKORDERS.saveBtn}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid={WORKORDERS.detailDialog}>
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading"><ClipboardList className="h-5 w-5 text-forest-mid" /> {detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={detail.status} />
                  <span className="text-xs text-muted-foreground">Priority: {detail.priority}</span>
                  <span className="text-xs text-muted-foreground">· Assigned: {detail.assigned_to_name || "—"}</span>
                </div>
                {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}

                {/* lifecycle stepper */}
                <div className="flex flex-wrap gap-1">
                  {WO_STATUSES.map((s) => (
                    <span key={s} className={`rounded px-2 py-0.5 text-[10px] font-semibold capitalize ${WO_STATUSES.indexOf(s) <= WO_STATUSES.indexOf(detail.status) ? "bg-forest-mid text-white" : "bg-slate-100 text-slate-400"}`}>{prettyStatus(s)}</span>
                  ))}
                </div>

                {detail.capture && (
                  <Card className="border-sage-light bg-sage-surface p-4">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-forest-mid"><HardHat className="h-3.5 w-3.5" /> Field capture</p>
                    <p className="mt-1 text-xs text-muted-foreground">By {detail.capture.submitted_by_name} · {fmtDateTime(detail.capture.submitted_at)}</p>
                    {detail.capture.quantities?.length > 0 && <div className="mt-2 text-sm"><b>Quantities:</b> {detail.capture.quantities.map((q) => `${q.quantity} ${q.unit} ${q.item}`).join(", ")}</div>}
                    {detail.capture.labour?.length > 0 && <div className="text-sm"><b>Labour:</b> {detail.capture.labour.map((l) => `${l.workers}× ${l.role} (${l.hours}h)`).join(", ")}</div>}
                    {detail.capture.notes && <div className="text-sm text-muted-foreground">{detail.capture.notes}</div>}
                  </Card>
                )}

                {detail.photos?.length > 0 && (
                  <div>
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Camera className="h-3.5 w-3.5" /> Photos</p>
                    {detail.photos.some((p) => p.quality?.flag && p.quality.flag !== "ok") && (
                      <p className="mb-2 flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                        <AlertTriangle className="h-3.5 w-3.5" /> AI flagged {detail.photos.filter((p) => p.quality?.flag && p.quality.flag !== "ok").length} photo(s) for quality — review before verifying.
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      {detail.photos.map((p) => (
                        <div key={p.id} className="relative">
                          <img src={photoUrl(p)} alt="evidence" className="h-24 w-full rounded-lg border object-cover" />
                          {p.quality?.flag && p.quality.flag !== "ok" && (
                            <span className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-amber-500 px-1 py-0.5 text-[9px] font-semibold capitalize text-white" title={p.quality.message || p.quality.flag} data-testid={WORKORDERS.photoFlag(p.id)}>
                              <AlertTriangle className="h-2.5 w-2.5" /> {p.quality.flag}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detail.pairs?.length > 0 && (
                  <div>
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><ArrowLeftRight className="h-3.5 w-3.5" /> Before / after</p>
                    <div className="space-y-3">
                      {detail.pairs.map((pr) => (
                        <div key={pr.id}>
                          {pr.caption && <p className="mb-1 text-xs text-muted-foreground">{pr.caption}</p>}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="relative">{pr.before && <img src={photoUrl(pr.before)} className="h-28 w-full rounded-lg border object-cover" alt="" />}<span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 text-[9px] font-semibold text-white">BEFORE</span></div>
                            <div className="relative">{pr.after && <img src={photoUrl(pr.after)} className="h-28 w-full rounded-lg border object-cover" alt="" />}<span className="absolute left-1 top-1 rounded bg-forest-mid px-1.5 text-[9px] font-semibold text-white">AFTER</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canEdit && (
                  <div className="flex gap-2 border-t pt-4">
                    {!["completed", "verified", "closed"].includes(detail.status) && (
                      <Button onClick={() => advance(detail)} className="bg-forest-mid hover:bg-forest-dark" data-testid={WORKORDERS.advanceBtn(detail.id)}>
                        <ArrowRight className="mr-1.5 h-4 w-4" /> Advance to {prettyStatus(WO_STATUSES[WO_STATUSES.indexOf(detail.status) + 1])}
                      </Button>
                    )}
                    {detail.status === "completed" && canVerify && (
                      <Button onClick={() => verify(detail)} className="bg-forest-mid hover:bg-forest-dark" data-testid={WORKORDERS.verifyBtn(detail.id)}>
                        <CheckCircle2 className="mr-1.5 h-4 w-4" /> Verify work
                      </Button>
                    )}
                    {detail.status === "verified" && <Button variant="outline" onClick={() => api.patch(`/work-orders/${detail.id}`, { status: "closed" }).then(() => { toast.success("Closed"); load(); openDetail(detail.id); })}>Close</Button>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
